import { useCallback, useEffect, useRef, useState } from "react";
import { useGetImageAssets } from "../../assets/hooks/use-get-image-assets";
import { useUploadImage } from "../../unlayer-email-content/hooks/use-upload-image";
import { fixUnlayerHtmlEntities } from "../../unlayer-email-content/utils/fix-unlayer-html-entities";

import type { DisplayConditionReturn } from "../../unlayer-email-content/hooks/use-display-condition";
import type { UnlayerDesign } from "../../unlayer-email-content/types/unlayer-design";
import type { UnlayerUpload } from "../../unlayer-email-content/types/unlayer-upload";
import type { RefObject } from "react";
import type { EditorRef, EmailEditorProps as UnlayerEmailEditorProps } from "react-email-editor";

interface UseFollowUpUnlayerEditorOptions {
  initialDesign: UnlayerDesign | null;
  onChange: (design: UnlayerDesign, html: string) => void;
  displayCondition: DisplayConditionReturn;
}

export function useFollowUpUnlayerEditor({
  initialDesign,
  onChange,
  displayCondition,
}: UseFollowUpUnlayerEditorOptions): {
  editor: RefObject<EditorRef | null>;
  dirty: boolean;
  save: () => void;
  onReady: UnlayerEmailEditorProps["onReady"];
  displayCondition: DisplayConditionReturn;
  reset: () => void;
} {
  const editor = useRef<EditorRef>(null);
  const [dirty, setDirty] = useState(false);

  // Use a ref for onChange so the design:updated listener (registered once in onReady)
  // always calls the latest callback, avoiding stale closure issues
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const uploadImage = useUploadImage();
  const getImageAssets = useGetImageAssets();

  const onSave = useCallback((rawHtml: string, design: UnlayerDesign) => {
    const html = fixUnlayerHtmlEntities(rawHtml);
    onChangeRef.current(design, html);
  }, []);

  const save = useCallback(() => {
    editor?.current?.editor?.exportHtml(({ html, design: emailDesign }) => {
      onSave(html, emailDesign);
      setDirty(false);
    });
  }, [onSave]);

  const onReady: UnlayerEmailEditorProps["onReady"] = useCallback(
    (unlayer: NonNullable<EditorRef["editor"]>) => {
      unlayer.registerCallback("displayCondition", displayCondition.onDisplayConditionRequest);

      if (uploadImage) unlayer.registerCallback("image", uploadImage);
      if (initialDesign) unlayer.loadDesign(initialDesign);

      editor?.current?.editor?.exportHtml(({ html, design: emailDesign }) => {
        onSave(html, emailDesign);
        setDirty(false);
      });

      unlayer.addEventListener("design:updated", () => {
        setDirty(true);
        editor?.current?.editor?.exportHtml(({ html, design: emailDesign }) => {
          onSave(html, emailDesign);
          setDirty(false);
        });
      });

      unlayer.registerProvider(
        "userUploads",
        (
          params: { page: number; searchText: string },
          done: (
            images: UnlayerUpload[],
            pagination: { hasMore: boolean; total: number; page: number; perPage: number },
          ) => void,
        ) => {
          const search = params.searchText.trim().toLowerCase();

          // oxlint-disable-next-line promise/always-return -- Unlayer provider; result delivered via done()
          void getImageAssets().then((contentLibraryAssets) => {
            const images = contentLibraryAssets
              ?.filter(
                (x) =>
                  !search ||
                  x.title.trim().toLowerCase().includes(search) ||
                  x.content_type?.trim().toLowerCase().includes(search),
              )
              .map((asset) => ({
                id: asset.id,
                location: asset.public_url,
                contentType: asset.mime_type,
                source: "user",
                // oxlint-disable-next-line typescript/no-unsafe-type-assertion
              })) as UnlayerUpload[];

            const page = params.page || 1;
            const perPage = 20;
            const total = images.length;
            const hasMore = total > page * perPage;

            // oxlint-disable-next-line promise/no-callback-in-promise
            done(images.slice((page - 1) * perPage, page * perPage), {
              hasMore,
              total,
              page,
              perPage,
            });
          });
        },
      );
    },
    [displayCondition, initialDesign, uploadImage, onSave, getImageAssets],
  );

  return {
    editor,
    dirty,
    save,
    onReady,
    displayCondition,
    reset: () => {
      if (initialDesign) editor?.current?.editor?.loadDesign(initialDesign);
      setDirty(false);
    },
  };
}
